import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { 
  Globe, 
  ShoppingCart, 
  Palette, 
  Search, 
  Mail, 
  Star,
  CheckCircle,
  ArrowRight,
  Zap,
  Users,
  TrendingUp,
  MessageCircle,
  Phone,
  MapPin,
  Clock
} from 'lucide-react';

const Index = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    service: '',
    message: ''
  });

  useEffect(() => {
    setIsVisible(true);
  }, []);

  const services = [
    {
      icon: Globe,
      title: "One-Page Websites",
      description: "Clean, mobile-friendly sites that showcase your business professionally",
      features: ["About page", "Gallery showcase", "WhatsApp integration", "Mobile responsive"],
      price: "Starting from R2,500",
      timeline: "5-7 business days",
      popular: false
    },
    {
      icon: ShoppingCart,
      title: "E-Commerce Websites",
      description: "Complete online stores with payment processing and inventory management",
      features: ["Payment gateway", "Product management", "SEO optimization", "Analytics dashboard"],
      price: "Starting from R8,500",
      timeline: "10-14 business days",
      popular: true
    },
    {
      icon: Search,
      title: "SEO & Marketing",
      description: "Get found on Google and attract the right customers to your business",
      features: ["Keyword optimization", "Local SEO", "Google My Business", "Performance tracking"],
      price: "Starting from R1,500/month",
      timeline: "Ongoing support",
      popular: false
    },
    {
      icon: Palette,
      title: "Branding & Design",
      description: "Professional logos and brand identity that makes your business stand out",
      features: ["Logo design", "Brand guidelines", "Social media templates", "Business cards"],
      price: "Starting from R3,500",
      timeline: "7-10 business days",
      popular: false
    }
  ];

  const testimonials = [
    {
      name: "Sarah Johnson",
      business: "Bloom Boutique",
      rating: 5,
      text: "TSDigitalPro transformed our online presence completely. Sales increased by 300% in just 3 months!",
      image: "https://images.unsplash.com/photo-1651924010862-2d59c96a8dc9?w=400&auto=format&fit=crop&q=80"
    },
    {
      name: "Michael Chen",
      business: "Tech Solutions SA",
      rating: 5,
      text: "Professional, fast, and exactly what we needed. The team understood our vision perfectly.",
      image: "https://images.unsplash.com/photo-1632647896118-f13930710cad?w=400&auto=format&fit=crop&q=80"
    },
    {
      name: "Priya Patel",
      business: "Spice Route Restaurant",
      rating: 5,
      text: "Our new website brings in customers daily. The WhatsApp integration is a game-changer!",
      image: "https://images.unsplash.com/photo-1579746662038-2b1a7bafa266?w=400&auto=format&fit=crop&q=80"
    }
  ];

  const stats = [
    { number: "150+", label: "Happy Clients" },
    { number: "300+", label: "Projects Completed" },
    { number: "98%", label: "Client Satisfaction" },
    { number: "24/7", label: "Support Available" }
  ];

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    // Handle form submission
    const whatsappMessage = `Hi TSDigitalPro! I'm interested in ${formData.service}. My name is ${formData.name} (${formData.email}). ${formData.message}`;
    const whatsappUrl = `https://wa.me/27123456789?text=${encodeURIComponent(whatsappMessage)}`;
    window.open(whatsappUrl, '_blank');
  };

  return (
    <div className="min-h-screen bg-background">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-white/95 backdrop-blur-sm border-b border-border z-50 transition-smooth">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-2">
              <div className="w-8 h-8 gradient-primary rounded-lg flex items-center justify-center">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <span className="text-xl font-bold text-gradient">TSDigitalPro</span>
            </div>
            <div className="hidden md:flex items-center space-x-8">
              <a href="#services" className="text-muted-foreground hover:text-primary transition-smooth">Services</a>
              <a href="#portfolio" className="text-muted-foreground hover:text-primary transition-smooth">Portfolio</a>
              <a href="#testimonials" className="text-muted-foreground hover:text-primary transition-smooth">Reviews</a>
              <a href="#contact" className="text-muted-foreground hover:text-primary transition-smooth">Contact</a>
              <Button className="gradient-primary text-white shadow-elegant hover:shadow-glow transition-smooth">
                Get Started
              </Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-24 pb-16 px-4 gradient-hero text-white relative overflow-hidden">
        <div className="absolute inset-0 bg-black/20"></div>
        <div className="container mx-auto relative z-10">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className={`space-y-8 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="space-y-4">
                <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30">
                  🚀 Trusted by 150+ South African Businesses
                </Badge>
                <h1 className="text-5xl lg:text-6xl font-bold leading-tight">
                  Take your <span className="text-yellow-300">business</span> online
                </h1>
                <p className="text-xl text-white/90 leading-relaxed">
                  We help small businesses, startups, and entrepreneurs build a strong online presence with digital solutions that are simple, modern, and results-driven.
                </p>
              </div>
              
              <div className="flex flex-col sm:flex-row gap-4">
                <Button size="lg" className="bg-white text-primary hover:bg-white/90 shadow-elegant transition-bounce">
                  Start Your Project
                  <ArrowRight className="ml-2 w-5 h-5" />
                </Button>
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  View Our Work
                </Button>
              </div>
              
              <div className="flex items-center space-x-8 pt-4">
                {stats.map((stat, index) => (
                  <div key={index} className="text-center">
                    <div className="text-2xl font-bold text-yellow-300">{stat.number}</div>
                    <div className="text-sm text-white/80">{stat.label}</div>
                  </div>
                ))}
              </div>
            </div>
            
            <div className={`relative transition-all duration-1000 delay-300 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1580894894513-541e068a3e2b?w=600&auto=format&fit=crop&q=80" 
                  alt="Professional workspace" 
                  className="rounded-2xl shadow-2xl animate-float"
                />
                <div className="absolute -bottom-6 -left-6 bg-white rounded-xl p-4 shadow-elegant">
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                      <TrendingUp className="w-6 h-6 text-green-600" />
                    </div>
                    <div>
                      <div className="text-sm font-semibold text-gray-900">300% Growth</div>
                      <div className="text-xs text-gray-600">Average client increase</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section id="services" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4">Our Services</Badge>
            <h2 className="text-4xl font-bold mb-4">Everything you need to succeed online</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              From stunning websites to powerful marketing strategies, we've got your digital transformation covered.
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <Card key={index} className={`relative gradient-card shadow-card hover:shadow-elegant transition-all duration-300 hover:-translate-y-2 ${service.popular ? 'ring-2 ring-primary' : ''}`}>
                  {service.popular && (
                    <Badge className="absolute -top-3 left-1/2 transform -translate-x-1/2 gradient-primary text-white">
                      Most Popular
                    </Badge>
                  )}
                  <CardHeader className="text-center">
                    <div className="w-16 h-16 gradient-primary rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-glow">
                      <IconComponent className="w-8 h-8 text-white" />
                    </div>
                    <CardTitle className="text-xl">{service.title}</CardTitle>
                    <CardDescription className="text-center">{service.description}</CardDescription>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="space-y-2">
                      {service.features.map((feature, idx) => (
                        <div key={idx} className="flex items-center space-x-2">
                          <CheckCircle className="w-4 h-4 text-green-500" />
                          <span className="text-sm">{feature}</span>
                        </div>
                      ))}
                    </div>
                    <div className="pt-4 border-t">
                      <div className="text-lg font-semibold text-primary">{service.price}</div>
                      <div className="text-sm text-muted-foreground flex items-center">
                        <Clock className="w-4 h-4 mr-1" />
                        {service.timeline}
                      </div>
                    </div>
                    <Button className="w-full gradient-primary text-white hover:shadow-glow transition-smooth">
                      Get Started
                    </Button>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-20 px-4 bg-muted/30">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <Badge className="mb-4">Why Choose TSDigitalPro</Badge>
              <h2 className="text-4xl font-bold mb-6">We don't just build websites, we build success stories</h2>
              <p className="text-lg text-muted-foreground mb-8">
                With over 150 successful projects and a 98% client satisfaction rate, we understand what it takes to make your business thrive online.
              </p>
              
              <div className="space-y-6">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center flex-shrink-0">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Lightning Fast Delivery</h3>
                    <p className="text-muted-foreground">Get your website live in as little as 5-7 days. We understand that time is money in business.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center flex-shrink-0">
                    <Users className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Dedicated Support Team</h3>
                    <p className="text-muted-foreground">24/7 support and ongoing maintenance to ensure your website always performs at its best.</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 gradient-primary rounded-xl flex items-center justify-center flex-shrink-0">
                    <TrendingUp className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h3 className="text-xl font-semibold mb-2">Proven Results</h3>
                    <p className="text-muted-foreground">Our clients see an average of 300% increase in online inquiries within the first 3 months.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="relative">
              <img 
                src="https://images.unsplash.com/photo-1565688527174-775059ac429c?w=600&auto=format&fit=crop&q=80" 
                alt="Team collaboration" 
                className="rounded-2xl shadow-2xl"
              />
              <div className="absolute -top-6 -right-6 bg-white rounded-xl p-6 shadow-elegant">
                <div className="text-center">
                  <div className="text-3xl font-bold text-primary">98%</div>
                  <div className="text-sm text-muted-foreground">Client Satisfaction</div>
                  <div className="flex justify-center mt-2">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} className="w-4 h-4 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 px-4">
        <div className="container mx-auto">
          <div className="text-center mb-16">
            <Badge className="mb-4">Client Success Stories</Badge>
            <h2 className="text-4xl font-bold mb-4">What our clients say about us</h2>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Don't just take our word for it. Here's what real business owners have to say about working with TSDigitalPro.
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="gradient-card shadow-card hover:shadow-elegant transition-all duration-300 hover:-translate-y-1">
                <CardContent className="p-6">
                  <div className="flex items-center mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-muted-foreground mb-6 italic">"{testimonial.text}"</p>
                  <div className="flex items-center space-x-3">
                    <img 
                      src={testimonial.image} 
                      alt={testimonial.name}
                      className="w-12 h-12 rounded-full object-cover"
                    />
                    <div>
                      <div className="font-semibold">{testimonial.name}</div>
                      <div className="text-sm text-muted-foreground">{testimonial.business}</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 px-4 gradient-hero text-white">
        <div className="container mx-auto">
          <div className="grid lg:grid-cols-2 gap-16">
            <div>
              <Badge className="bg-white/20 text-white border-white/30 mb-4">
                Get In Touch
              </Badge>
              <h2 className="text-4xl font-bold mb-6">Ready to transform your business online?</h2>
              <p className="text-xl text-white/90 mb-8">
                Let's discuss your project and see how we can help you achieve your digital goals. Get a free consultation today!
              </p>
              
              <div className="space-y-6">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Phone className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold">Call Us</div>
                    <div className="text-white/80">+27 12 345 6789</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <Mail className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold">Email Us</div>
                    <div className="text-white/80">hello@tsdigitalpro.com</div>
                  </div>
                </div>
                
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <MapPin className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <div className="font-semibold">Visit Us</div>
                    <div className="text-white/80">Johannesburg, South Africa</div>
                  </div>
                </div>
              </div>
            </div>
            
            <Card className="bg-white/10 backdrop-blur-sm border-white/20">
              <CardHeader>
                <CardTitle className="text-white">Get Your Free Quote</CardTitle>
                <CardDescription className="text-white/80">
                  Fill out the form below and we'll get back to you within 24 hours.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <Input 
                      placeholder="Your Name" 
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
                      required
                    />
                    <Input 
                      type="email" 
                      placeholder="Your Email" 
                      value={formData.email}
                      onChange={(e) => setFormData({...formData, email: e.target.value})}
                      className="bg-white/10 border-white/20 text-white placeholder:text-white/60"
                      required
                    />
                  </div>
                  <select 
                    value={formData.service}
                    onChange={(e) => setFormData({...formData, service: e.target.value})}
                    className="w-full p-3 rounded-md bg-white/10 border border-white/20 text-white"
                    required
                  >
                    <option value="" className="text-gray-900">Select a service</option>
                    <option value="One-Page Website" className="text-gray-900">One-Page Website</option>
                    <option value="E-Commerce Website" className="text-gray-900">E-Commerce Website</option>
                    <option value="SEO & Marketing" className="text-gray-900">SEO & Marketing</option>
                    <option value="Branding & Design" className="text-gray-900">Branding & Design</option>
                  </select>
                  <Textarea 
                    placeholder="Tell us about your project..." 
                    value={formData.message}
                    onChange={(e) => setFormData({...formData, message: e.target.value})}
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/60 min-h-[100px]"
                    required
                  />
                  <Button type="submit" className="w-full bg-white text-primary hover:bg-white/90 shadow-elegant transition-bounce">
                    Send Message via WhatsApp
                    <MessageCircle className="ml-2 w-5 h-5" />
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-12 px-4">
        <div className="container mx-auto">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="flex items-center space-x-2 mb-4">
                <div className="w-8 h-8 gradient-primary rounded-lg flex items-center justify-center">
                  <Zap className="w-5 h-5 text-white" />
                </div>
                <span className="text-xl font-bold">TSDigitalPro</span>
              </div>
              <p className="text-gray-400 mb-4">
                Helping South African businesses succeed online with modern, results-driven digital solutions.
              </p>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Services</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-smooth">Website Development</a></li>
                <li><a href="#" className="hover:text-white transition-smooth">E-Commerce Solutions</a></li>
                <li><a href="#" className="hover:text-white transition-smooth">SEO & Marketing</a></li>
                <li><a href="#" className="hover:text-white transition-smooth">Branding & Design</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Company</h3>
              <ul className="space-y-2 text-gray-400">
                <li><a href="#" className="hover:text-white transition-smooth">About Us</a></li>
                <li><a href="#" className="hover:text-white transition-smooth">Portfolio</a></li>
                <li><a href="#" className="hover:text-white transition-smooth">Testimonials</a></li>
                <li><a href="#" className="hover:text-white transition-smooth">Contact</a></li>
              </ul>
            </div>
            
            <div>
              <h3 className="font-semibold mb-4">Contact Info</h3>
              <ul className="space-y-2 text-gray-400">
                <li>+27 12 345 6789</li>
                <li>hello@tsdigitalpro.com</li>
                <li>Johannesburg, South Africa</li>
              </ul>
            </div>
          </div>
          
          <div className="border-t border-gray-800 mt-8 pt-8 text-center text-gray-400">
            <p>&copy; 2024 TSDigitalPro. All rights reserved. Built with ❤️ in South Africa.</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Index;
